import { BundleItemDetailsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { Product } from '@/ui/components/pages/products/types/Product';
import { Bundle } from '@/ui/services/BundleService';
import { DocShapeId } from '@/utils/shapeIds';
import { ProductRuleService } from '@/ui/services/product-rule-service';

type ListingFields = Pick<Product, 'introText' | 'category' | 'categoryKey' | 'categoryTitle'>;

// The bundle stays on the entry: the card picks its variants, image, price, link and badges from it.
function mapBundleToProduct(
  bundleInfo: BundleItemDetailsFragment,
  priceVariantId: string | undefined,
  listing: ListingFields,
  productRuleService: ProductRuleService,
): Product {
  const bundle = new Bundle(bundleInfo, priceVariantId);
  return {
    ...listing,
    id: bundleInfo.id,
    path: bundle.getLinkPath() ?? '',
    name: bundle.name,
    price: bundle.finalPrice || 0,
    priceWithoutDiscount: bundle.totalOriginalPrice || 0,
    image: bundle.images[0],
    // The default set's badges, used by the overview filters; the card follows its chosen colour
    badges: productRuleService.getBundleBadges(bundle.getConfiguration()),
    bundle,
  };
}

/**
 * Builds the product overview cards from the listing page's "category-section" chunks. Each
 * section is one category, and each of its slots links to one of:
 * - a tiles page: card from its listing image, name and excerpt, without a price
 * - a product with listed variants: the cheapest variant's price and image, plus its badges from
 *   the product rules
 * - bundles: one card per bundle, which keeps the bundle so the card can switch its colour
 * Also returns the currency of the area's price variant.
 */
export function extractProducts(
  components: any[],
  priceVariantId: string | undefined,
  productRuleService: ProductRuleService = new ProductRuleService(),
): { products: Product[]; currency: string } {
  const categorySections = components.filter((comp: any) => comp.id === 'category-section');
  let currency = '';
  const products = categorySections?.flatMap((section: any) => {
    if (section.content.chunks) {
      return section.content.chunks?.flatMap((chunk: any[]) => {
        let category = '';
        let categoryKey = '';
        let categoryTitle = '';

        chunk.forEach((item: any) => {
          if (item.id === 'category-name') {
            category = item.content?.text;
          }
          if (item.id === 'category') {
            categoryKey = item.content?.options[0].key;
          }
          if (item.id === 'category-title') {
            categoryTitle = item.content;
          }
        });
        return chunk?.flatMap((item: any) => {
          if (item.id === 'product-and-text') {
            const productsComponents = item.content.components;
            return productsComponents[0].content.chunks?.flatMap((productChunk: any[]) => {
              const productItem = productChunk.find((p: any) => p.id === 'product');
              const textItem = productChunk.find((p: any) => p.id === 'text');

              const relatedItem = productItem?.content?.items?.[0];
              if (relatedItem?.shape?.identifier === DocShapeId.pageTilesType) {
                return {
                  id: relatedItem.id || '',
                  path: relatedItem.path || '',
                  name: relatedItem.pageName?.content?.text || relatedItem.name || '',
                  introText:
                    relatedItem.excerpt?.content?.text || textItem?.content?.plainText?.[0] || '',
                  price: 0,
                  priceWithoutDiscount: 0,
                  category,
                  categoryKey,
                  categoryTitle,
                  image: relatedItem.listingCategoryImage?.content?.images?.[0],
                  badges: [],
                };
              }

              if (textItem) {
                if (productItem && productItem.content?.productVariants) {
                  const productVariants = productItem.content?.productVariants || [];

                  if (productVariants.length > 0) {
                    let lowestPrice = Infinity;
                    let selectedVariantImage;

                    productVariants.forEach((variant: any) => {
                      const priceVariants = variant.priceVariants || [];
                      const matchingPriceVariants = priceVariants.filter(
                        (pv: any) => pv.identifier === priceVariantId,
                      );

                      const currentLowestPriceVariant = matchingPriceVariants.reduce(
                        (lowest: any, current: any) =>
                          (current.price < lowest.price ? current : lowest),
                        matchingPriceVariants[0], // Initialize with the first matching price variant
                      );
                      if (
                        currentLowestPriceVariant &&
                        currentLowestPriceVariant.identifier === priceVariantId &&
                        currentLowestPriceVariant.price < lowestPrice
                      ) {
                        lowestPrice = currentLowestPriceVariant.price;
                        currency = currentLowestPriceVariant.currency || '';
                        selectedVariantImage = variant.images?.[0];
                      }
                    });
                    if (lowestPrice < Infinity) {
                      return {
                        id: productItem.content.items?.[0]?.id || '',
                        path: productItem.content.items?.[0]?.path || '',
                        name: productItem.content.items?.[0]?.name || '',
                        introText: textItem.content?.plainText?.[0] || '',
                        price: lowestPrice,
                        category,
                        categoryKey,
                        categoryTitle,
                        image: selectedVariantImage,
                        badges: productRuleService.getListedProductBadges(
                          productItem.content.items?.[0]?.id ?? '',
                          productVariants.map((variant: any) => variant.sku),
                        ),
                      };
                    }
                  }
                } else if (productItem?.content?.items?.length) {
                  return productItem.content.items.map((bundleInfo: BundleItemDetailsFragment) =>
                    mapBundleToProduct(
                      bundleInfo,
                      priceVariantId,
                      {
                        introText: textItem.content?.plainText[0] || '',
                        category,
                        categoryKey,
                        categoryTitle,
                      },
                      productRuleService,
                    ),
                  );
                }
              }

              return [];
            });
          }
          return [];
        });
      });
    }
    return [];
  });

  return {
    products,
    currency,
  };
}
